import { useState, useMemo, ChangeEvent, FC, useEffect } from 'react';

import { Typography } from '@legacy/components/mui-components';
import { Stack, Box, Divider } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { AccountingPeriodConfiguration } from '@/api/REST/accountingPeriods';
import { CustomSelect, SelectOption } from '@/components/global/CustomSelect';

// Custom styled Radio component with blue color
const BlueRadio = styled(Radio)(({ theme: _theme }) => ({
  '&.Mui-checked': {
    color: '#4976fe', // Your selected-year-blue color
  },
  '&:hover': {
    backgroundColor: 'rgba(73, 118, 254, 0.04)', // Blue with low opacity
  },
}));

interface AccountingPeriodModeSelectorProps {
  configuration: AccountingPeriodConfiguration | undefined;
  onConfigurationChange: (configuration: AccountingPeriodConfiguration) => void;
  onExecuteAutomaticMode?: (
    configuration: AccountingPeriodConfiguration,
    options?: {
      onBeforeExecute?: () => void | Promise<void>;
      onCancel?: () => void | Promise<void>;
    },
  ) => void;
  availablePeriodTypes?: string[];
  isLoading?: boolean;
}

/**
 * Simple radio button selector for accounting period mode
 * Replaces ToggleTabs with a cleaner radio button interface
 * Uses custom blue color for radio buttons
 * Shows period type selector when automatic mode is selected
 * Now uses API configuration instead of local state
 */
const AccountingPeriodModeSelector: FC<AccountingPeriodModeSelectorProps> = ({
  configuration,
  onConfigurationChange,
  onExecuteAutomaticMode,
  availablePeriodTypes,
  isLoading = false,
}) => {
  const { t } = useTranslation('systemAdministrationNavigation');
  // Local state for mode and period type during pending changes
  const [selectedMode, setSelectedMode] = useState<'manual' | 'automatic'>(
    'manual',
  );
  const [localPeriodType, setLocalPeriodType] = useState<string>('');
  const [pendingAutomaticSelection, setPendingAutomaticSelection] =
    useState<boolean>(false);
  // Helper functions to convert between API format and UI format
  const getModeFromConfiguration = (
    config: AccountingPeriodConfiguration | undefined,
  ): 'manual' | 'automatic' => {
    if (!config) return 'manual';
    return config.accountingPeriodAutomaticCreation === '1'
      ? 'automatic'
      : 'manual';
  };

  const getMonthFromConfiguration = (
    config: AccountingPeriodConfiguration | undefined,
  ): string => {
    if (!config || !config.fiscalYearStartDate) return '';

    // Parse the date string to extract month
    try {
      const date = new Date(config.fiscalYearStartDate);
      return (date.getMonth() + 1).toString().padStart(2, '0'); // Convert to MM format
    } catch {
      return '';
    }
  };

  const getPeriodTypeFromConfiguration = (
    config: AccountingPeriodConfiguration | undefined,
  ): string => {
    if (!config || !config.accountingPeriodType) return '';
    return config.accountingPeriodType;
  };

  const currentMode = getModeFromConfiguration(configuration);
  const currentMonth = getMonthFromConfiguration(configuration);
  const configPeriodType = getPeriodTypeFromConfiguration(configuration);

  useEffect(() => {
    const modeFromConfiguration = getModeFromConfiguration(configuration);

    if (pendingAutomaticSelection && modeFromConfiguration === 'manual') {
      return;
    }

    setSelectedMode(modeFromConfiguration);

    if (modeFromConfiguration === 'automatic') {
      setPendingAutomaticSelection(false);
    }
  }, [
    configuration?.accountingPeriodAutomaticCreation,
    configuration,
    pendingAutomaticSelection,
  ]);

  useEffect(() => {
    setLocalPeriodType(configPeriodType);
  }, [configuration?.accountingPeriodType, configPeriodType]);

  const handleModeChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!configuration) return;

    const newMode = event.target.value as 'manual' | 'automatic';
    const currentMode = getModeFromConfiguration(configuration);
    const isPending = pendingAutomaticSelection;

    if (newMode === 'manual') {
      setSelectedMode('manual');
      setPendingAutomaticSelection(false);
      setLocalPeriodType(configPeriodType);

      if (currentMode !== 'manual') {
        onConfigurationChange({
          ...configuration,
          accountingPeriodAutomaticCreation: '0',
        });
      }
      return;
    }

    // newMode === 'automatic'
    setSelectedMode('automatic');
    setLocalPeriodType(configPeriodType);

    if (currentMode !== 'automatic') {
      setPendingAutomaticSelection(true);
    } else if (!isPending) {
      setPendingAutomaticSelection(false);
    }
  };

  const handleMonthChange = (month: string | number) => {
    if (!configuration) return;

    const monthValue = typeof month === 'number' ? month.toString() : month;

    // Create a date string with the selected month
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const day = 1; // First day of the month

    const newDate = new Date(year, parseInt(monthValue, 10) - 1, day);
    const formattedDate = newDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    onConfigurationChange({
      ...configuration,
      fiscalYearStartDate: formattedDate,
    });
  };

  const handlePeriodTypeChange = (periodType: string | number) => {
    if (!configuration) return;
    if (selectedMode !== 'automatic') return;

    const periodTypeValue =
      typeof periodType === 'number' ? periodType.toString() : periodType;

    // Update local state immediately for UI responsiveness
    setLocalPeriodType(periodTypeValue);

    const updatedConfiguration = {
      ...configuration,
      accountingPeriodType: periodTypeValue,
      accountingPeriodAutomaticCreation: '1',
    };
    const wasPending = pendingAutomaticSelection || currentMode === 'manual';

    if (onExecuteAutomaticMode) {
      onExecuteAutomaticMode(updatedConfiguration, {
        onBeforeExecute: () => {
          setPendingAutomaticSelection(false);
          return onConfigurationChange(updatedConfiguration);
        },
        onCancel: () => {
          if (wasPending) {
            setSelectedMode('manual');
            setLocalPeriodType(configPeriodType);
          } else {
            setLocalPeriodType(configPeriodType);
          }
          setPendingAutomaticSelection(false);
        },
      });
    } else {
      onConfigurationChange(updatedConfiguration);
    }
  };

  const currentPeriodType =
    selectedMode === 'automatic'
      ? localPeriodType || configPeriodType
      : configPeriodType;

  // Month options for fiscal year start
  const monthOptions: SelectOption[] = [
    { value: '01', label: t('SystemAdministrationAccountingPeriodJanuary') },
    { value: '02', label: t('SystemAdministrationAccountingPeriodFebruary') },
    { value: '03', label: t('SystemAdministrationAccountingPeriodMarch') },
    { value: '04', label: t('SystemAdministrationAccountingPeriodApril') },
    { value: '05', label: t('SystemAdministrationAccountingPeriodMay') },
    { value: '06', label: t('SystemAdministrationAccountingPeriodJune') },
    { value: '07', label: t('SystemAdministrationAccountingPeriodJuly') },
    { value: '08', label: t('SystemAdministrationAccountingPeriodAugust') },
    { value: '09', label: t('SystemAdministrationAccountingPeriodSeptember') },
    { value: '10', label: t('SystemAdministrationAccountingPeriodOctober') },
    { value: '11', label: t('SystemAdministrationAccountingPeriodNovember') },
    { value: '12', label: t('SystemAdministrationAccountingPeriodDecember') },
  ];

  // Period type options for automatic mode - use dynamic values from API
  const periodTypeOptions: SelectOption[] = useMemo(() => {
    if (availablePeriodTypes && availablePeriodTypes.length > 0) {
      return availablePeriodTypes.map((type) => ({
        value: type,
        label: type,
      }));
    }

    // Return empty array if API doesn't provide period types
    return [];
  }, [availablePeriodTypes]);

  return (
    <Stack gap={2}>
      {/* Fiscal Year Selection - Always visible */}
      <Stack>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('SystemAdministrationAccountingPeriodFiscalYear')}
        </Typography>
        <CustomSelect
          label={t('SystemAdministrationAccountingPeriodStartDate')}
          value={currentMonth}
          onChange={handleMonthChange}
          options={monthOptions}
          placeholder="Select start month"
          size="small"
          labelPosition="left"
          labelColor="#000000"
          labelSize="medium"
          sx={{ maxWidth: 240 }}
          disabled={isLoading}
          data-automation-id="AccountingPeriodStartMonthDropdown"
        />
        <Divider sx={{ mt: 2, borderColor: 'divider' }} />
      </Stack>

      {/* Accounting Period Mode Selection */}
      <FormControl>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('SystemAdministrationAccountingPeriodAccountingPeriods')}
        </Typography>
        <RadioGroup
          row
          name="accounting-period-mode"
          value={selectedMode}
          onChange={handleModeChange}
        >
          <FormControlLabel
            value="automatic"
            control={<BlueRadio />}
            label={t('SystemAdministrationAccountingPeriodAutomatic')}
            disabled={isLoading}
          />
          <FormControlLabel
            value="manual"
            control={<BlueRadio />}
            label={t('SystemAdministrationAccountingPeriodManual')}
            disabled={isLoading}
          />
        </RadioGroup>

        {/* Warning Note */}
        <Typography variant="body1">
          {t('SystemAdministrationAccountingPeriodConfigurationWarning')}
        </Typography>

        {/* Period Type Selection - Only shown when Automatic mode is selected and period types are available */}
        {selectedMode === 'automatic' && periodTypeOptions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              {t('SystemAdministrationAccountingPeriodType')}
            </Typography>
            <CustomSelect
              label={t('SystemAdministrationAccountingPeriodPeriodType')}
              value={currentPeriodType}
              onChange={handlePeriodTypeChange}
              options={periodTypeOptions}
              placeholder="Select period type"
              size="small"
              labelPosition="left"
              labelColor="#000000"
              labelSize="medium"
              sx={{ maxWidth: 240 }}
              disabled={isLoading}
              data-automation-id="AccountingPeriodTypeDropdown"
            />
          </Box>
        )}
      </FormControl>
    </Stack>
  );
};

export default AccountingPeriodModeSelector;
